import React from 'react';
import {
    Box,
    Chip,
    Divider,
    InputAdornment,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {makeRequest} from "../../../services/requestService";
import {API_URL} from "../../../constants";
import EditIcon from "@mui/icons-material/Edit";
import {Link as RouterLink, useHistory, useLocation} from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";

const ITEMS_PER_PAGE = 10;

async function fetchUsers(page = 1) {
    const url = `${API_URL}/users?page=${page}&size=${ITEMS_PER_PAGE}`;
    const response = await makeRequest(url);
    if (response.status !== 200) return [];
    return response.data;
}

async function searchUsers(search, page = 1) {
    const url = `${API_URL}/users/search?page=${page}&username=${encodeURI(search)}&size=${ITEMS_PER_PAGE}`;
    const response = await makeRequest(url);
    if (response.status !== 200) return [];
    return response.data;
}

export default function Users(props) {
    const location = useLocation();
    const history = useHistory();
    const {t} = useTranslation("common");
    const [users, setUsers] = React.useState([]);
    const [totalItems, setTotalItems] = React.useState(0);

    const {page, search} = React.useMemo(() => {
        const query = new URLSearchParams(location.search);
        const page = parseInt(query.get("page") ?? "0");
        const search = query.get("search");
        return {page, search}
    }, [location.search])

    React.useEffect(async () => {
        let res;

        if (search) {
            res = await searchUsers(search, page);
        } else {
            res = await fetchUsers(page);
        }

        setUsers(res.data);
        setTotalItems(res.totalItems);
    }, [page, search]);

    function handleChangeSearch(value) {
        const query = new URLSearchParams(location.search);
        if (value === "") {
            query.delete("search");
            query.set("page", "0");
        } else if (search === null && value !== "") {
            query.set("page", "0");
            query.set("search", value);
        } else {
            query.set("search", value);
        }

        history.push("?" + query.toString());
    }

    function handleChangePage(value) {
        const query = new URLSearchParams(location.search);
        query.set("page", value);
        history.push("?" + query.toString());
    }

    return (
        <Stack spacing={2}>
            <Typography variant="h6" fontSize={22} fontWeight="bold" color="textSecondary">
                {t("main.pages.admin.users.title")}
            </Typography>
            <Divider/>
            <Box display="flex" justifyContent="flex-end">
                <TextField
                    onChange={(e) => handleChangeSearch(e.target.value)}
                    value={search}
                    label={t("main.pages.admin.users.search.label")}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment>
                    }}
                />
            </Box>
            <Paper>
                <TableContainer sx={{maxHeight: 600}}>
                    <Table stickyHeader size="small" aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell key="id">
                                </TableCell>
                                <TableCell key="username">
                                    {t('main.components.form.placeholder.username')}
                                </TableCell>
                                <TableCell>
                                    {t('main.components.form.placeholder.pseudo')}
                                </TableCell>
                                <TableCell key="email">
                                    {t('main.components.form.placeholder.email')}
                                </TableCell>
                                <TableCell key="roles">
                                    {t('main.components.form.placeholder.roles')}
                                </TableCell>
                                <TableCell key="actions">
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...users.entries()].map(([id, row]) => {
                                return (
                                    <TableRow key={id} hover role="checkbox" tabIndex={-1}
                                              sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                    >
                                        <TableCell key="id">
                                            {row.id}
                                        </TableCell>
                                        <TableCell key="username">
                                            {row.name}
                                        </TableCell>
                                        <TableCell key="displayName">
                                            {row.displayName}
                                        </TableCell>
                                        <TableCell key="email">
                                            {row.email}
                                        </TableCell>
                                        <TableCell key="roles">
                                            {row.roles.map(r => <Chip
                                                label={t(`common.user.roles.${r.name.toLowerCase()}`)}/>)}
                                        </TableCell>
                                        <TableCell key="actions">
                                            <Link component={RouterLink} to={`/admin/users/${row.id}`}>
                                                <EditIcon/>
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    size="small"
                    rowsPerPageOptions={[ITEMS_PER_PAGE]}
                    component="div"
                    count={totalItems}
                    rowsPerPage={ITEMS_PER_PAGE}
                    page={page}
                    onPageChange={(_, page) => handleChangePage(page)}
                />
            </Paper>
        </Stack>
    );
}
